import classNames from "classnames/dedupe";
import PropTypes from "prop-types";
import React, { useRef, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { useOnClickOutside } from "../../helpers";

/**
 *
 * @typedef {object} DropdownButtonTheme
 * @property {string} [backgroundColour]
 * @property {string} [textColour]
 * @property {string} [borderColour]
 * @property {string} [disabledBackgroundColour]
 * @property {string} [disabledTextColour]
 * @property {string} [disabledBorderColour]
 */

/**
 *
 * @param {Object} props
 * @param {{ label?: string }[]} [props.options]
 * @param {(option: { label?: string }) => string} [props.getOptionLabel]
 * @param {(option: { label?: string }) => any} [props.onChange]
 * @param {string} [props.title]
 * @param {JSX.Element} [props.icon]
 * @param {string} [props.className]
 * @param {DropdownButtonTheme} [props.theme]
 * @param {string} [props.height]
 * @param {string} [props.width]
 * @param {boolean} [props.disabled]
 * @returns {JSX.Element}
 */
export const DropdownButton = ({
  options,
  getOptionLabel = opt => opt.label,
  onChange,
  title,
  className,
  icon,
  theme,
  height,
  width,
  disabled
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef();
  useOnClickOutside(dropdownRef, () => {
    setShowDropdown(false);
  });
  const classes = classNames(className, {
    "bg-white": disabled && !theme?.disabledBackgroundColour || !disabled && !theme?.backgroundColour,
    "text-en-yellow-700": !disabled && !theme?.textColour,
    "text-en-gray-200": disabled && !theme?.disabledTextColour,
    "border-en-yellow-700": !disabled && !theme?.borderColour,
    "border-en-gray-200": disabled && !theme?.disabledBorderColour
  }, !disabled && theme?.backgroundColour || "", !disabled && theme?.textColour || "", !disabled && theme?.borderColour || "", disabled && theme?.disabledBackgroundColour || "", disabled && theme?.disabledTextColour || "", disabled && theme?.disabledBorderColour || "", "rounded text-center flex items-center border-2");
  const onDropdownClicked = () => {
    !disabled && setShowDropdown(!showDropdown);
  };
  const onOptionChanged = option => {
    onChange(option);
    setShowDropdown(false);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "relative width-fit-content"
  }, /*#__PURE__*/React.createElement("button", {
    className: classes,
    style: {
      ...(width ? {
        width
      } : {}),
      ...(height ? {
        height
      } : {})
    },
    disabled: disabled,
    onClick: onDropdownClicked
  }, /*#__PURE__*/React.createElement("div", {
    className: "pl-2 py-1"
  }, icon), /*#__PURE__*/React.createElement("div", {
    className: "px-2 py-2 truncate font-bold",
    style: {
      ...(width ? {
        width
      } : {})
    }
  }, title), /*#__PURE__*/React.createElement("div", {
    className: "pr-2 py-2 font-bold"
  }, /*#__PURE__*/React.createElement(FaChevronDown, null))), showDropdown && /*#__PURE__*/React.createElement("div", {
    className: "bg-white border border-black rounded text-black z-10 width-fit-content absolute right-0",
    ref: dropdownRef
  }, options.map((option, index) => /*#__PURE__*/React.createElement("div", {
    key: `option-${index}`,
    className: `px-2 py-1 text-center cursor-pointer ${index === options.length - 1 ? "" : "border-b"}`,
    onClick: () => {
      onOptionChanged(option);
    }
  }, getOptionLabel(option)))));
};
DropdownButton.propTypes = {
  options: PropTypes.array.isRequired,
  getOptionLabel: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  className: PropTypes.string,
  icon: PropTypes.element,
  theme: PropTypes.shape({
    backgroundColour: PropTypes.string,
    textColour: PropTypes.string,
    borderColour: PropTypes.string,
    disabledBackgroundColour: PropTypes.string,
    disabledTextColour: PropTypes.string,
    disabledBorderColour: PropTypes.string
  }),
  height: PropTypes.string,
  width: PropTypes.string,
  disabled: PropTypes.bool
};